import { useState } from 'react';
import { AuthProvider } from '../auth/auth-context.js';
import type { AuthActivationRoute } from '../auth/auth-activation.js';
import { useAuth } from '../auth/use-auth.js';
import { getSupabaseBrowserClient } from '../auth/supabase-client.js';
import { AuthActivationScreen } from './AuthActivationScreen.js';
import { AuthGate } from './AuthGate.js';
import { BrandMark } from './BrandMark.js';

function AuthRouteBoundary({ initialRoute }: { initialRoute: AuthActivationRoute | null }) {
  const [route, setRoute] = useState(initialRoute);
  const auth = useAuth();
  const leaveActivationRoute = () => {
    window.history.replaceState(window.history.state, '', '/');
    setRoute(null);
  };
  const cancelActivation = async () => {
    await auth.logout();
    leaveActivationRoute();
  };
  if (route) {
    return (
      <AuthActivationScreen
        route={route}
        onVerify={auth.verifyActivation}
        onUpdatePassword={auth.updatePassword}
        onComplete={leaveActivationRoute}
        onCancel={() => void cancelActivation()}
      />
    );
  }
  return <AuthGate />;
}

export function App({ initialAuthRoute = null }: { initialAuthRoute?: AuthActivationRoute | null }) {
  try {
    const client = getSupabaseBrowserClient();
    return <AuthProvider client={client}><AuthRouteBoundary initialRoute={initialAuthRoute} /></AuthProvider>;
  } catch {
    return (
      <main className="system-state" aria-labelledby="config-error-title">
        <div className="system-state__panel">
          <BrandMark />
          <p className="eyebrow">Configuration required</p>
          <h1 id="config-error-title">Browser authentication is unavailable</h1>
          <p>The required public authentication settings were not provided or are invalid.</p>
        </div>
      </main>
    );
  }
}
